import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'TestWisely - Online Toetsplatform | Digitaal Toetsen voor Voortgezet Onderwijs',
  description = 'TestWisely is het online toetsplatform voor middelbare scholen. Digitale toets maken met AI nakijken, beveiligd toetsen op iPad, live monitoring, fraudedetectie en Kennisnet Entree. Formatief en summatief toetsen op één plek.',
  keywords = 'online toetsplatform, digitaal toetsen, toetsplatform voortgezet onderwijs, online toetsen maken, toetssoftware school, AI nakijken, automatisch nakijken, beveiligd toetsen, Safe Exam Browser, formatief toetsen, summatief toetsen, toets afnemen, vragenbank, toetsbibliotheek, itembank, Kennisnet Entree, iPad toets app, live monitoring, fraudedetectie, anti-fraude toetsen, digitale toets, online examen, toetsen VO, lockdown browser, toetsmatrijs, leerdoelen toetsen, RTTI, toetsplatform docenten, TestWisely',
  ogImage = '/testwise-og.png',
  ogType = 'website',
  canonical,
  breadcrumbs,
  structuredData,
  noIndex = false,
  // lang: taal van de pagina ('nl' | 'en'). alternates: { nl, en } met paden of
  // volledige URL's van dezelfde pagina in beide talen; dan komen er hreflang-
  // links voor nl, en en x-default (= nl).
  lang = 'nl',
  alternates
}) => {
  const siteUrl = 'https://testwisely.nl';
  // Paden én volledige URL's accepteren. Eerder gaven de werken-bij-pagina's
  // een volledige URL mee en werd dat "https://testwisely.nlhttps://...".
  const absoluut = (u) => (/^https?:\/\//.test(u) ? u : `${siteUrl}${u}`);
  const fullCanonical = canonical ? absoluut(canonical) : siteUrl;
  const hreflangs = alternates
    ? [
      { taal: 'nl', href: absoluut(alternates.nl) },
      { taal: 'en', href: absoluut(alternates.en) },
      { taal: 'x-default', href: absoluut(alternates.nl) },
    ]
    : [
      { taal: 'nl', href: fullCanonical },
      { taal: 'x-default', href: fullCanonical },
    ];
  const fullOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  // Organization structured data
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'TestWisely',
    alternateName: 'Test Wisely',
    description: 'Online toetsplatform voor het voortgezet onderwijs. Digitaal toetsen met AI nakijken, beveiligde iPad app, live monitoring en Kennisnet Entree.',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/tw-logo-04.png`,
      width: 512,
      height: 512
    },
    image: `${siteUrl}/testwise-og.png`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Bolstoen 11-N',
      addressLocality: 'Amsterdam',
      postalCode: '1046 AS',
      addressRegion: 'Noord-Holland',
      addressCountry: 'NL'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 52.3452,
      longitude: 4.8120
    },
    areaServed: {
      '@type': 'Country',
      name: 'Netherlands'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'info@testwisely.nl',
      contactType: 'customer service',
      availableLanguage: 'Dutch'
    },
    founder: {
      '@type': 'Person',
      name: 'Yasar Kocdas'
    },
    knowsAbout: [
      'digitaal toetsen',
      'online toetsplatform',
      'AI nakijken',
      'formatief toetsen',
      'summatief toetsen',
      'voortgezet onderwijs',
      'beveiligd toetsen'
    ]
  };

  // WebSite + SearchAction schema (voor sitelinks searchbox in Google)
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'TestWisely',
    url: siteUrl,
    description: 'Online toetsplatform voor het voortgezet onderwijs',
    publisher: { '@id': `${siteUrl}/#organization` },
    inLanguage: 'nl-NL',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/veelgestelde-vragen?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  // SoftwareApplication schema
  const softwareData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'TestWisely',
    applicationCategory: 'EducationalApplication',
    operatingSystem: 'Web, iOS, iPadOS, Windows',
    // Geen Offer met prijs 0: er is geen gratis versie. Een school betaalt een vaste
    // vergoeding plus een bedrag per leerling, via een offerte (Voorwaarden.js, 3.4).
    description: 'Online toetsplatform voor het voortgezet onderwijs. Digitaal toetsen met AI nakijken, beveiligde iPad app, live monitoring en Kennisnet Entree.',
    url: siteUrl,
    publisher: { '@id': `${siteUrl}/#organization` },
    featureList: [
      'AI-gestuurd nakijken van open vragen',
      'Beveiligde iPad app voor toetsen',
      'Live monitoring van leerlingen',
      'Fraudedetectie en anti-spieken',
      'Kennisnet Entree SSO integratie',
      'Formatief en summatief toetsen',
      'Uitgebreide toetsstatistieken',
      'Vragenbank en itembank'
    ]
  };

  // BreadcrumbList schema (optioneel, per pagina)
  const breadcrumbData = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? absoluut(item.url) : undefined
    }))
  } : null;

  // structuredData kan een object of array zijn
  const structuredDataItems = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData])
    : [softwareData];

  // react-snap rendert de pagina in een browser met userAgent 'ReactSnap' en
  // bakt de head-tags hieronder in de HTML. Na het laden maakt React (createRoot,
  // React 19 + react-helmet-async 3) eigen exemplaren en laat de ingebakken
  // staan: na JavaScript stond alles dubbel, en na doorklikken bleven title,
  // description en canonical van de eerste pagina staan (19-10-2026). Daarom
  // krijgen de ingebakken tags een merkteken en ruimen we ze op zodra React de
  // eigen tags heeft gezet.
  const voorrender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';
  const merk = voorrender ? { 'data-voorgerenderd': '' } : {};

  // index.html heeft een vaste <title> voor pagina's zonder SEO-component
  // (#standaard-titel); die gaat ook weg zodra een pagina zijn eigen titel heeft.
  useEffect(() => {
    document.getElementById('standaard-titel')?.remove();
    if (!voorrender) {
      document.head.querySelectorAll('[data-voorgerenderd]').forEach((el) => el.remove());
    }
  }, [voorrender]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title {...merk}>{title}</title>
      <meta {...merk} name="title" content={title} />
      <meta {...merk} name="description" content={description} />
      <meta {...merk} name="keywords" content={keywords} />
      <link {...merk} rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta {...merk} property="og:type" content={ogType} />
      <meta {...merk} property="og:url" content={fullCanonical} />
      <meta {...merk} property="og:title" content={title} />
      <meta {...merk} property="og:description" content={description} />
      <meta {...merk} property="og:image" content={fullOgImage} />
      <meta {...merk} property="og:image:width" content="1200" />
      <meta {...merk} property="og:image:height" content="630" />
      <meta {...merk} property="og:site_name" content="TestWisely" />
      <meta {...merk} property="og:locale" content={lang === 'en' ? 'en_GB' : 'nl_NL'} />

      {/* Twitter */}
      <meta {...merk} property="twitter:card" content="summary_large_image" />
      <meta {...merk} property="twitter:url" content={fullCanonical} />
      <meta {...merk} property="twitter:title" content={title} />
      <meta {...merk} property="twitter:description" content={description} />
      <meta {...merk} property="twitter:image" content={fullOgImage} />

      {/* Language */}
      {hreflangs.map((h) => (
        <link {...merk} key={`hreflang-${h.taal}`} rel="alternate" hrefLang={h.taal} href={h.href} />
      ))}

      {/* Additional SEO Meta Tags */}
      <meta {...merk} name="robots" content={noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
      <meta {...merk} name="language" content={lang === 'en' ? 'English' : 'Dutch'} />
      <meta {...merk} name="revisit-after" content="7 days" />
      <meta {...merk} httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta {...merk} name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* author en geo.* staan al in public/index.html (gelijk op elke pagina). */}

      {/* JSON-LD: Page-specific structured data */}
      {structuredDataItems.map((sd, i) => (
        <script key={`sd-${i}`} type="application/ld+json">
          {JSON.stringify(sd)}
        </script>
      ))}

      {/* JSON-LD: Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>

      {/* JSON-LD: WebSite + SearchAction */}
      <script type="application/ld+json">
        {JSON.stringify(websiteData)}
      </script>

      {/* JSON-LD: Breadcrumbs (als meegegeven) */}
      {breadcrumbData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
